import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaBadge from "@/components/jea/JeaBadge";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const Section = ({ label, children, testid }) => (
  <div className="mb-8" data-testid={testid}>
    <div className="jea-caption text-accent-pink mb-2 pb-2 border-b border-border-default">{label}</div>
    {children}
  </div>
);

const PasswordInput = ({ value, onChange, placeholder, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <JeaInput
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-accent-pink"
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testid}-toggle`}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

const ProfilePanel = () => {
  const { user, refreshMe, logout } = useAuth();
  const [name, setName] = useState(user?.full_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [dz, setDz] = useState({ open: false, currentPw: "", emailConfirm: "", busy: false });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    setName(user?.full_name || "");
    setEmail(user?.email || "");
  }, [user]);

  const dirty = name !== (user?.full_name || "") || email !== (user?.email || "");

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiClient.patch("/users/me", { full_name: name.trim(), email: email.trim() });
      toast.success("Profile updated.");
      if (refreshMe) await refreshMe();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingProfile(false); }
  };

  const pwValid =
    pw.next.length >= 8 &&
    /\d/.test(pw.next) &&
    /[A-Za-z]/.test(pw.next) &&
    pw.next === pw.confirm &&
    pw.current.length > 0;

  const changePassword = async () => {
    setSavingPw(true);
    try {
      await apiClient.post("/users/me/change-password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingPw(false); }
  };

  const deleteAccount = async () => {
    setDz((s) => ({ ...s, busy: true }));
    try {
      await apiClient.delete("/users/me", {
        data: { current_password: dz.currentPw, email_confirm: dz.emailConfirm },
      });
      toast.success("Account deleted. Logging out.");
      setTimeout(() => logout?.(), 800);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setDz((s) => ({ ...s, busy: false })); }
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <div className="space-y-6">
      <JeaCard data-testid="profile-panel">
        <Section label="Account Info" testid="profile-account-info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="jea-caption mb-1.5">Full Name</div>
              <JeaInput value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>
            <div>
              <div className="jea-caption mb-1.5">Email</div>
              <div className="relative">
                <JeaInput value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" />
                <span
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.08em] bg-success/15 text-success border border-success/40"
                  data-testid="profile-email-verified"
                >
                  <CheckCircle2 size={10} /> VERIFIED
                </span>
              </div>
            </div>
            <div>
              <div className="jea-caption mb-1.5">Account Type</div>
              <JeaBadge variant={user?.account_type === "pro" ? "pink-tier" : "silver"} data-testid="profile-account-type">
                {(user?.account_type || "standard").toUpperCase()}
              </JeaBadge>
            </div>
            <div>
              <div className="jea-caption mb-1.5">Member Since</div>
              <div className="jea-mono text-text-secondary text-[14px]" data-testid="profile-member-since">
                {memberSince}
              </div>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <JeaButton onClick={saveProfile} disabled={!dirty} loading={savingProfile} data-testid="profile-save">
              Save Changes
            </JeaButton>
          </div>
        </Section>

        <Section label="Change Password" testid="profile-change-password">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div className="jea-caption mb-1.5">Current Password</div>
              <PasswordInput value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} testid="pw-current" />
            </div>
            <div>
              <div className="jea-caption mb-1.5">New Password</div>
              <PasswordInput value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} testid="pw-new" />
              <div className="text-text-muted text-[11px] mt-1">
                Min 8 chars, must contain a letter + a number.
              </div>
            </div>
            <div>
              <div className="jea-caption mb-1.5">Confirm New Password</div>
              <PasswordInput value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} testid="pw-confirm" />
              {pw.confirm.length > 0 && pw.confirm !== pw.next && (
                <div className="text-danger text-[11px] mt-1">Passwords don't match.</div>
              )}
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <JeaButton onClick={changePassword} disabled={!pwValid} loading={savingPw} data-testid="pw-submit">
              Update Password
            </JeaButton>
          </div>
        </Section>

        {!user?.is_admin && (
          <div
            className="rounded-[8px] p-6 border-l-4 border-danger"
            style={{ background: "rgba(231, 76, 60, 0.05)" }}
            data-testid="profile-danger-zone"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-danger shrink-0 mt-1" size={20} />
              <div className="flex-1">
                <h3 className="text-danger font-bold text-[15px]">Delete Account</h3>
                <p className="text-text-secondary text-[13px] mt-1">
                  Permanently delete your account and all associated data. Active subscriptions must be canceled first.
                </p>
              </div>
              <JeaButton
                size="sm"
                variant="danger"
                onClick={() => setDz({ open: true, currentPw: "", emailConfirm: "", busy: false })}
                data-testid="delete-account-open"
              >
                Delete Account
              </JeaButton>
            </div>
          </div>
        )}
      </JeaCard>

      {dz.open && (
        <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
             onClick={dz.busy ? undefined : () => setDz({ ...dz, open: false })}
             data-testid="delete-account-modal">
          <div className="w-full max-w-[440px] bg-bg-secondary border border-danger/40 rounded-[12px] p-6"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-danger font-bold text-[18px]">Delete your account?</h3>
            <p className="text-text-secondary text-[13px] mt-2">
              This is permanent. Type your email <span className="jea-mono">{user?.email}</span> to confirm.
            </p>
            <div className="mt-4 space-y-3">
              <PasswordInput value={dz.currentPw} onChange={(e) => setDz((s) => ({ ...s, currentPw: e.target.value }))} placeholder="Current password" testid="delete-account-pw" />
              <JeaInput value={dz.emailConfirm} onChange={(e) => setDz((s) => ({ ...s, emailConfirm: e.target.value }))} placeholder="Confirm your email" data-testid="delete-account-email" />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDz({ ...dz, open: false })}
                className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold"
                data-testid="delete-account-cancel"
              >
                Cancel
              </button>
              <JeaButton
                size="sm"
                variant="danger"
                onClick={deleteAccount}
                loading={dz.busy}
                disabled={!dz.currentPw || dz.emailConfirm.trim().toLowerCase() !== (user?.email || "")}
                data-testid="delete-account-confirm"
              >
                Permanently Delete
              </JeaButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePanel;
